import React, {useState, useEffect, useRef, useCallback, useContext} from 'react'
import {ThemeContext} from 'styled-components/macro'
import {AiSourceLink} from "purview-design/components/AiSourceLink"
import {Dialog, Button} from "@startlibs/components"
import {RecordFileBadge} from "purview-design/components/RecordFileBadge"
import {getFilenameFromRecord} from "../RecordRowActions"
import {useIntl} from "react-intl"
import {getColor} from "@startlibs/utils"
import {
  SourceImageWrap,
  SourceImage,
  SourceImageCaption,
  AiSummaryText,
  SummaryTitle,
  SummarySectionTitle,
  SummarySubsectionTitle,
  SummaryParagraph,
  SummaryKeyValue,
  SummaryKeyLabel,
  SummaryBulletList,
  SummaryBulletItem,
  ModalSummaryScrollOuter,
  ModalSummaryScroll,
  ModalSummaryScrollFade,
  ModalSummaryScrollTopFade,
} from './AiSummaryStyles'

// ── Helpers ──────────────────────────────────────────────────────────────────

export const getRefLabel = (pages, documentName) =>
  pages?.length ? `Page ${pages.join(', ')}` : (documentName || 'Document')

// ── Source image components ──────────────────────────────────────────────────

export const ImageSourceRef = ({refData, eveDocumentId, authJwt}) => {
  const [imgSrc, setImgSrc] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!eveDocumentId || !authJwt || !refData?.pictureRef) return
    let cancelled = false
    setLoading(true)
    setError(false)

    const token = typeof authJwt === 'function' ? authJwt() : authJwt
    fetch(`/api/eve/documents/${eveDocumentId}/pictures/${refData.pictureRef}`, {
      headers: {'Authorization': `Bearer ${token}`},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) return
        setImgSrc(URL.createObjectURL(blob))
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [eveDocumentId, authJwt, refData?.pictureRef])

  if (!refData?.pictureRef) {
    return <AiSourceLink label={`Page ${refData?.pages?.join(', ') || '?'}`} sources={[{filename: `Page ${refData?.pages?.join(', ') || '?'}`, excerpt: refData?.text || ''}]} />
  }

  return (
    <SourceImageWrap>
      {loading && <SourceImageCaption>Loading image...</SourceImageCaption>}
      {error && <SourceImageCaption>{refData.text || 'Image could not be loaded'}</SourceImageCaption>}
      {imgSrc && <SourceImage src={imgSrc} alt={refData.text || 'Source image'} />}
      {refData.text && imgSrc && <SourceImageCaption>{refData.text}</SourceImageCaption>}
    </SourceImageWrap>
  )
}

// Wrapper that fetches pictures for multiple refs and passes imageUrls to one AiSourceLink chip
export const AiSourceLinkWithImages = ({refsData, onOpenPage, eveDocumentId, authJwt, documentName}) => {
  const [imageUrls, setImageUrls] = useState({})

  const pictureRefsKey = refsData.map(r => r?.pictureRef || '').join(',')

  useEffect(() => {
    if (!eveDocumentId || !authJwt) return
    const cancelFns = []
    refsData.forEach(refData => {
      if (!refData?.pictureRef) return
      let cancelled = false
      cancelFns.push(() => { cancelled = true })
      const token = typeof authJwt === 'function' ? authJwt() : authJwt
      fetch(`/api/eve/documents/${eveDocumentId}/pictures/${refData.pictureRef}`, {
        headers: {'Authorization': `Bearer ${token}`},
      })
        .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.blob() })
        .then(blob => { if (!cancelled) setImageUrls(prev => ({...prev, [refData.pictureRef]: URL.createObjectURL(blob)})) })
        .catch(() => {})
    })
    return () => cancelFns.forEach(fn => fn())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pictureRefsKey, eveDocumentId, authJwt])

  const label = getRefLabel(refsData[0]?.pages, documentName)
  const sources = refsData.map(refData => ({
    filename: getRefLabel(refData.pages, documentName),
    excerpt: refData.text || '',
    imageUrl: imageUrls[refData.pictureRef] || undefined,
  }))

  return (
    <AiSourceLink
      label={label}
      sources={sources}
      onOpen={onOpenPage ? (source) => {
        const idx = sources.indexOf(source)
        const refData = refsData[idx >= 0 ? idx : 0]
        onOpenPage(refData.pages?.[0] ?? null, null)
      } : undefined}
    />
  )
}

// Wrapper that fetches picture for a ref and passes imageUrl to AiSourceLink
export const AiSourceLinkWithImage = ({refData, onOpenPage, eveDocumentId, authJwt, documentName}) => {
  const [imageUrl, setImageUrl] = useState(null)
  const firstPage = refData.pages?.[0] ?? null

  useEffect(() => {
    if (!refData?.pictureRef || !eveDocumentId || !authJwt) return
    let cancelled = false
    const token = typeof authJwt === 'function' ? authJwt() : authJwt
    fetch(`/api/eve/documents/${eveDocumentId}/pictures/${refData.pictureRef}`, {
      headers: {'Authorization': `Bearer ${token}`},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        if (!cancelled) setImageUrl(URL.createObjectURL(blob))
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [refData?.pictureRef, eveDocumentId, authJwt])

  const label = getRefLabel(refData.pages, documentName)
  const sources = [{
    filename: label,
    excerpt: refData.text || '',
    imageUrl: imageUrl || undefined,
  }]

  return (
    <AiSourceLink
      label={label}
      sources={sources}
      onOpen={onOpenPage ? () => onOpenPage(firstPage) : undefined}
    />
  )
}

// ── Text + citation rendering ─────────────────────────────────────────────────

export const renderTextWithRefs = (text, references, onOpenPage, eveDocumentId, authJwt, documentName) => {
  if (!text) return null
  // Split into tokens: ref markers and text segments
  const parts = text.split(/(\[\[\d+\]\])/)

  // Group adjacent markers (separated only by empty/whitespace text)
  const grouped = []
  let pendingMarkers = []

  const flushMarkers = () => {
    if (pendingMarkers.length === 0) return
    // Check if any have pictureRef (needs special component)
    const hasPicture = pendingMarkers.some(m => m.refData?.pictureRef && eveDocumentId && authJwt)
    if (hasPicture && pendingMarkers.length === 1) {
      const m = pendingMarkers[0]
      grouped.push({type: 'imageRef', refData: m.refData, key: m.key})
    } else if (hasPicture) {
      // Multiple adjacent image refs — one chip, multiple sources with images
      grouped.push({type: 'imageRefGroup', markers: pendingMarkers, key: pendingMarkers[0].key})
    } else {
      grouped.push({type: 'refGroup', markers: pendingMarkers})
    }
    pendingMarkers = []
  }

  parts.forEach((part, i) => {
    const match = part.match(/^\[\[(\d+)\]\]$/)
    if (match) {
      const marker = Number(match[1])
      const refData = references?.find(r => r.marker === marker)
      pendingMarkers.push({marker, refData, key: i})
    } else if (part.trim() === '' && pendingMarkers.length > 0) {
      // Empty/whitespace between markers — keep grouping
    } else {
      flushMarkers()
      if (part) grouped.push({type: 'text', text: part, key: i})
    }
  })
  flushMarkers()

  return grouped.map((item, idx) => {
    if (item.type === 'text') {
      return <React.Fragment key={item.key}>{item.text}</React.Fragment>
    }
    if (item.type === 'imageRef') {
      return <AiSourceLinkWithImage key={item.key} refData={item.refData} onOpenPage={onOpenPage} eveDocumentId={eveDocumentId} authJwt={authJwt} documentName={documentName} />
    }
    if (item.type === 'imageRefGroup') {
      return <AiSourceLinkWithImages key={item.key} refsData={item.markers.map(m => m.refData).filter(Boolean)} onOpenPage={onOpenPage} eveDocumentId={eveDocumentId} authJwt={authJwt} documentName={documentName} />
    }
    // refGroup — single AiSourceLink with multiple sources
    const {markers} = item
    const validMarkers = markers.filter(m => m.refData)
    if (validMarkers.length === 0) {
      return <span key={idx} style={{fontSize: 10, color: 'rgba(0,141,208,0.9)'}}>[{markers.map(m => m.marker).join(', ')}]</span>
    }
    const sources = validMarkers.map(m => ({
      filename: getRefLabel(m.refData.pages, documentName),
      excerpt: m.refData.text || '',
    }))
    const normalizeQuotes = (str) => str
      .replace(/[‘’ʼ`´]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/[–—]/g, '-')
    const normalizeForViewerHighlight = (str) => normalizeQuotes(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[()\[\]{}]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    const getHighlightText = (refData) => {
      const page = refData.pages?.[0] ?? null
      if (page != null || !refData.text) return null
      const normalized = normalizeForViewerHighlight(refData.text)
      const words = normalized.split(/\s+/).filter(Boolean)
      const startWords = words.slice(0, 10).join(' ')
      const endWords = words.slice(-10).join(' ')
      return words.length > 20 && startWords !== endWords
        ? `${startWords}|||${endWords}`
        : words.slice(0, 15).join(' ')
    }
    const label = validMarkers.length === 1
      ? getRefLabel(validMarkers[0].refData.pages, documentName)
      : getRefLabel(validMarkers[0].refData.pages?.slice(0, 1), documentName)
    return <AiSourceLink
      key={markers[0].key}
      label={label}
      sources={sources}
      onOpen={onOpenPage ? (source) => {
        const idx = sources.indexOf(source)
        const refData = validMarkers[idx >= 0 ? idx : 0].refData
        onOpenPage(refData.pages?.[0] ?? null, getHighlightText(refData))
      } : undefined}
    />
  })
}

export const renderBlocks = (blocks, references, onOpenPage, eveDocumentId, authJwt, documentName) => {
  if (!blocks || !blocks.length) return null

  const result = []
  let bulletBuffer = []

  const flushBullets = () => {
    if (bulletBuffer.length) {
      result.push(
        <SummaryBulletList key={`bl-${result.length}`}>
          {bulletBuffer.map((b, j) => (
            <SummaryBulletItem key={j}>{renderTextWithRefs(b.text, references, onOpenPage, eveDocumentId, authJwt, documentName)}</SummaryBulletItem>
          ))}
        </SummaryBulletList>
      )
      bulletBuffer = []
    }
  }

  blocks.forEach((block, i) => {
    if (block.kind === 'BULLET') {
      bulletBuffer.push(block)
    } else {
      flushBullets()
      if (block.kind === 'KEY_VALUE') {
        result.push(
          <SummaryKeyValue key={i}>
            <SummaryKeyLabel>{block.label}: </SummaryKeyLabel>
            {renderTextWithRefs(block.text, references, onOpenPage, eveDocumentId, authJwt, documentName)}
          </SummaryKeyValue>
        )
      } else {
        result.push(
          <SummaryParagraph key={i}>{renderTextWithRefs(block.text, references, onOpenPage, eveDocumentId, authJwt, documentName)}</SummaryParagraph>
        )
      }
    }
  })

  flushBullets()
  return result
}

export const renderSubsections = (subsections, references, onOpenPage, eveDocumentId, authJwt, documentName) => {
  if (!subsections || !subsections.length) return null
  return subsections.map((sub, i) => (
    <div key={i}>
      <SummarySubsectionTitle>{sub.title}</SummarySubsectionTitle>
      {renderBlocks(sub.blocks, references, onOpenPage, eveDocumentId, authJwt, documentName)}
      {renderSubsections(sub.subsections, references, onOpenPage, eveDocumentId, authJwt, documentName)}
    </div>
  ))
}

// ── StructuredSummaryRenderer ─────────────────────────────────────────────────

export const StructuredSummaryRenderer = ({data, onOpenPage, eveDocumentId, authJwt, documentName}) => {
  if (!data) return null

  const {title, overview, sections, conclusions, references} = data

  return (
    <>
      {title && <SummaryTitle>{title}</SummaryTitle>}
      {overview && (
        <div>
          {renderBlocks(overview.blocks, references, onOpenPage, eveDocumentId, authJwt, documentName)}
          {renderSubsections(overview.subsections, references, onOpenPage, eveDocumentId, authJwt, documentName)}
        </div>
      )}
      {sections && sections.map((section, i) => (
        <div key={i}>
          <SummarySectionTitle>{section.title}</SummarySectionTitle>
          {renderBlocks(section.blocks, references, onOpenPage, eveDocumentId, authJwt, documentName)}
          {renderSubsections(section.subsections, references, onOpenPage, eveDocumentId, authJwt, documentName)}
        </div>
      ))}
      {conclusions && (
        <div>
          <SummarySectionTitle>Conclusions</SummarySectionTitle>
          {typeof conclusions === 'string'
            ? <SummaryParagraph>{renderTextWithRefs(conclusions, references, onOpenPage, eveDocumentId, authJwt, documentName)}</SummaryParagraph>
            : Array.isArray(conclusions)
              ? renderBlocks(conclusions, references, onOpenPage, eveDocumentId, authJwt, documentName)
              : conclusions?.blocks
                ? renderBlocks(conclusions.blocks, references, onOpenPage, eveDocumentId, authJwt, documentName)
                : null
          }
        </div>
      )}
    </>
  )
}

// ── RelevanceBarSvg ───────────────────────────────────────────────────────────

// Gradient bar with a score indicator. The SVG is 149×12.
// The indicator triangle + line sit at (score/100)*149 from left.
export const RelevanceBarSvg = ({score}) => {
  const theme = useContext(ThemeContext)
  const barColor = theme?.colors?.relevanceBarColor || '#3AB8F3'
  const rawX = Math.round((score / 100) * 149)
  const x = Math.min(rawX, 141)
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="149" height="12" viewBox="0 0 149 12" fill="none">
      {/* indicator triangle pointing down */}
      <path
        d={`M${x+7.859} 0.157068C${x+7.953} 0.287958 ${x+8} 0.39267 ${x+8} 0.549738C${x+8} 0.706806 ${x+7.953} 0.837696 ${x+7.859} 0.942408L${x+4.353} 4.84293C${x+4.259} 4.94764 ${x+4.141} 5 ${x+4} 5C${x+3.859} 5 ${x+3.741} 4.94764 ${x+3.647} 4.84293L${x+0.141} 0.942408C${x+0.047} 0.837696 ${x} 0.706806 ${x} 0.549738C${x} 0.39267 ${x+0.047} 0.26178 ${x+0.141} 0.157068C${x+0.259} 0.052356 ${x+0.353} 0 ${x+0.494} 0H${x+7.482}C${x+7.647} 0 ${x+7.741} 0.052356 ${x+7.859} 0.157068Z`}
        fill="#444444"
      />
      {/* gradient track */}
      <rect y="9" width="149" height="3" rx="1.5" fill="url(#rlg)"/>
      {/* indicator line */}
      <line x1={x+4} y1="12" x2={x+4} y2="8" stroke="#444444"/>
      <defs>
        <linearGradient id="rlg" x1="0" y1="10.5" x2="149" y2="10.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="#BABABA"/>
          <stop offset="1" stopColor={barColor}/>
        </linearGradient>
      </defs>
    </svg>
  )
}

// ── OpenOutsideIcon ───────────────────────────────────────────────────────────

export const OpenOutsideIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 1h3v3M11 1 6 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

// ── AiSummaryModal ────────────────────────────────────────────────────────────

export const AiSummaryModal = ({paragraphs, summaryData, record, onClose, onOpenPage, eveDocumentId, authJwt}) => {
  const intl = useIntl()
  const fileName = getFilenameFromRecord(intl, record) || 'Unknown file'

  const scrollRef = useRef(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [atBottom, setAtBottom] = useState(false)
  const [isScrolledDown, setIsScrolledDown] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (el) {
      setHasOverflow(el.scrollHeight > el.clientHeight)
      setIsScrolledDown(el.scrollTop > 4)
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4)
    }
  }, [paragraphs, summaryData])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setIsScrolledDown(el.scrollTop > 4)
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4)
  }, [])

  return (
    <Dialog
      css="max-width: 96rem; width: 96rem;"
      closeDialog={onClose}
      title={
        <>
          AI Summary
          <RecordFileBadge
            recordFileName={fileName}
            recordClass={record.recordClass}
            fileExtension={record.fileExtension}
            css={`margin-left: 16px;`}
          />
        </>
      }
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <ModalSummaryScrollOuter>
        <ModalSummaryScroll ref={scrollRef} onScroll={handleScroll}>
          {summaryData && (summaryData.title || summaryData.sections) ? (
            <StructuredSummaryRenderer data={summaryData} onOpenPage={onOpenPage} eveDocumentId={eveDocumentId} authJwt={authJwt} documentName={fileName} />
          ) : (
            paragraphs.map((para, i) => (
              <AiSummaryText key={i}>
                {para}
              </AiSummaryText>
            ))
          )}
        </ModalSummaryScroll>
        {hasOverflow && isScrolledDown && <ModalSummaryScrollTopFade />}
        {hasOverflow && !atBottom && <ModalSummaryScrollFade />}
      </ModalSummaryScrollOuter>
    </Dialog>
  )
}
